// src/components/modals/MoveHistoryModals.tsx
import React from "react";
import { SavedMoveHistory } from "../../api/moveHistoryService.ts";

interface SaveHistoryDialogProps {
  show: boolean;
  saveName: string;
  setSaveName: (name: string) => void;
  errorMessage: string;
  isSaving: boolean;
  moveHistoryLength: number;
  currentGameId: string;
  boardRows: number;
  saveSuccess?: boolean;
  onSave: () => void;
  onClose: () => void;
}

export const SaveHistoryDialog: React.FC<SaveHistoryDialogProps> = ({
  show,
  saveName,
  setSaveName,
  errorMessage,
  isSaving,
  moveHistoryLength,
  currentGameId,
  boardRows,
  saveSuccess,
  onSave,
  onClose,
}) => {
  if (!show) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 2000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "8px",
          padding: "30px",
          maxWidth: "500px",
          width: "90%",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.3)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ marginTop: 0, color: "#333", textAlign: "center" }}>
          💾 Save Move History
        </h2>

        {saveSuccess ? (
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                padding: "20px",
                backgroundColor: "#e8f5e9",
                borderRadius: "8px",
                marginBottom: "20px",
                border: "2px solid #4CAF50",
              }}
            >
              <div style={{ fontSize: "48px", marginBottom: "10px" }}>✅</div>
              <div style={{ fontSize: "18px", fontWeight: "bold", color: "#2e7d32", marginBottom: "5px" }}>
                Game Saved Successfully!
              </div>
              <div style={{ fontSize: "14px", color: "#666" }}>
                {moveHistoryLength} moves • {boardRows}×{boardRows} board
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                padding: "10px 30px",
                backgroundColor: "#4CAF50",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "16px",
                fontWeight: "bold",
              }}
            >
              Close
            </button>
          </div>
        ) : (
          <>
            {errorMessage && (
              <div
                style={{
                  padding: "10px",
                  backgroundColor: "#ffebee",
                  color: "#c62828",
                  borderRadius: "4px",
                  marginBottom: "15px",
                  fontSize: "14px",
                }}
              >
                {errorMessage}
              </div>
            )}

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "bold",
                  color: "#333",
                }}
              >
                Game Name:
              </label>
              <input
                type="text"
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                placeholder="e.g., Epic Queen Sacrifice Game"
                maxLength={100}
                style={{
                  width: "100%",
                  padding: "10px",
                  borderRadius: "4px",
                  border: "2px solid #ddd",
                  fontSize: "14px",
                }}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    onSave();
                  }
                }}
              />
              <div
                style={{
                  fontSize: "12px",
                  color: "#666",
                  marginTop: "8px",
                }}
              >
                {moveHistoryLength} moves • {currentGameId} • {boardRows}×{boardRows}
              </div>
            </div>

            <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
              <button
                onClick={onSave}
                disabled={isSaving || !saveName.trim()}
                style={{
                  padding: "10px 20px",
                  backgroundColor: saveName.trim() && !isSaving ? "#4CAF50" : "#ccc",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: saveName.trim() && !isSaving ? "pointer" : "not-allowed",
                  fontSize: "16px",
                  fontWeight: "bold",
                }}
              >
                {isSaving ? "Saving..." : "Save"}
              </button>
              <button
                onClick={onClose}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#ff9800",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "16px",
                  fontWeight: "bold",
                }}
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

interface LoadHistoryDialogProps {
  show: boolean;
  savedHistories: SavedMoveHistory[];
  errorMessage: string;
  isLoading: boolean;
  onLoad: (history: SavedMoveHistory) => void;
  onDelete: (historyId: number, historyName: string) => void;
  onClose: () => void;
}

export const LoadHistoryDialog: React.FC<LoadHistoryDialogProps> = ({
  show,
  savedHistories,
  errorMessage,
  isLoading,
  onLoad,
  onDelete,
  onClose,
}) => {
  if (!show) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 2000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "8px",
          padding: "30px",
          maxWidth: "700px",
          width: "90%",
          maxHeight: "80vh",
          overflowY: "auto",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.3)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ marginTop: 0, color: "#333", textAlign: "center" }}>
          📁 Load Move History
        </h2>

        {errorMessage && (
          <div
            style={{
              padding: "10px",
              backgroundColor: "#ffebee",
              color: "#c62828",
              borderRadius: "4px",
              marginBottom: "15px",
              fontSize: "14px",
            }}
          >
            {errorMessage}
          </div>
        )}

        {isLoading ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <div>Loading saved histories...</div>
          </div>
        ) : savedHistories.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              color: "#666",
              fontStyle: "italic",
            }}
          >
            No saved histories found
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "10px",
            }}
          >
            {savedHistories.map((history) => (
              <div
                key={history.id}
                style={{
                  border: "2px solid #ddd",
                  borderRadius: "8px",
                  padding: "15px",
                  backgroundColor: "#f9f9f9",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#e3f2fd";
                  e.currentTarget.style.borderColor = "#2196F3";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#f9f9f9";
                  e.currentTarget.style.borderColor = "#ddd";
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: "8px",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: "16px",
                        fontWeight: "bold",
                        color: "#333",
                        marginBottom: "8px",
                      }}
                    >
                      {history.name}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#666",
                        marginBottom: "3px",
                      }}
                    >
                      {history.total_moves} moves • {history.game_preset} •{" "}
                      {history.board_size}×{history.board_size}
                    </div>
                    <div style={{ fontSize: "11px", color: "#999" }}>
                      Saved {new Date(history.created_at).toLocaleDateString()} at{" "}
                      {new Date(history.created_at).toLocaleTimeString()}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button
                      onClick={() => onLoad(history)}
                      style={{
                        padding: "6px 12px",
                        backgroundColor: "#2196F3",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: "bold",
                      }}
                    >
                      Load
                    </button>
                    <button
                      onClick={() => onDelete(history.id, history.name)}
                      style={{
                        padding: "6px 12px",
                        backgroundColor: "#f44336",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: "bold",
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop: "20px", textAlign: "center" }}>
          <button
            onClick={onClose}
            style={{
              padding: "10px 20px",
              backgroundColor: "#ff9800",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "16px",
              fontWeight: "bold",
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
